<template>
  <section class="proposals-approved">
    <form-input
      v-if="scrollable"
      v-model="search"
      class="proposals-approved-search"
      icon="search"
      name="search"
      placeholder="Pesquisar..."
      @input="searching"
    />

    <proposals-approved-filters
      :scrollable="scrollable"
      :per-page="perPage"
      class="proposals-approved-filters"
    />

    <proposals-approved-resume
      v-if="scrollable"
      class="proposals-approved-resume"
      actionless
    />

    <ul class="proposals-approved-list">
      <li
        v-for="proposal in list"
        :key="proposal.id"
        class="proposals-approved-item"
      >
        <router-link
          :to="link(proposal)"
          class="proposals-approved-link"
        >
          <typography-heading hierarchy="h3">
            {{ proposal.customer.name | namelize }}

            <template v-slot:subtitle>
              cadastrada dia {{ proposal.created_at | createdAt }}
            </template>
          </typography-heading>

          <information-line
            :label="vehicle(proposal)"
            :copiable="false"
            class="proposals-approved-line"
            valueless
          />

          <information-line
            :label="seller(proposal)"
            :value="store(proposal)"
            :copiable="false"
            class="proposals-approved-line proposals-approved-seller"
          />

          <information-line
            v-for="attempt in proposal.proposal_attemps"
            :key="attempt.id"
            class="proposals-approved-line proposals-approved-vertical"
          >
            <template v-slot:label>
              <span class="proposals-approved-label">
                {{ values(attempt) }}

                <context-tag
                  :color="integrated(attempt) ? 'success' : 'danger'"
                  class="proposals-approved-tag"
                >
                  {{ integrated(attempt) ? 'Integrada' : 'Não integrada' }}
                </context-tag>
              </span>
            </template>

            <proposals-approved-button
              :user="auth.user"
              :proposal="proposal"
              :attempt="attempt"
              class="proposals-approved-button"
            />
          </information-line>
        </router-link>

        <span class="proposals-approved-separator" />
      </li>

      <context-state
        :state="state"
        class="proposals-approved-state"
        tag="li"
      >
        <template v-slot:loading>
          Carregando propostas...
        </template>

        <template v-slot:notfound>
          Nenhuma proposta encontrada :(
        </template>

        <template v-slot:empty>
          Sem mais propostas
        </template>
      </context-state>
    </ul>
  </section>
</template>

<script>
  import { mapState } from 'vuex';

  import mobile from '@sdk/helpers/interface/mobile';
  import { installments } from '@sdk/helpers/misc/installments';
  import { find } from '@sdk/helpers/misc/formalization';
  import monetize from '@sdk/helpers/formatters/monetize';
  import namelize from '@sdk/helpers/formatters/namelize';
  import vehicleize from '@sdk/helpers/formatters/vehicleize';

  import FormInput from '@cordel/components/Form/Input.vue';

  import ContextState from '@cordel/components/Context/State.vue';
  import ContextTag from '@cordel/components/Context/Tag.vue';

  import InformationLine from '@cordel/components/Information/Line.vue';
  import TypographyHeading from '@cordel/components/Typography/Heading.vue';

  import ProposalsApprovedFilter from '@sdk/views/Proposals/Approved/Filter.vue';
  import ProposalsApprovedFilters from '@sdk/views/Proposals/Approved/Filters.vue';
  import ProposalsApprovedResume from '@sdk/views/Proposals/Approved/Resume.vue';
  import ProposalsApprovedButton from '@sdk/views/Proposals/Approved/Button.vue';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import { INITIAL_PROPOSAL_FILTERS as INITIAL } from '@/store/modules/proposals';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import sort from '@/_CRUDL/helpers/data/sort';

  export default {
    name: 'ProposalsApproved',
    components: {
      FormInput,

      ContextState,
      ContextTag,

      InformationLine,
      TypographyHeading,

      ProposalsApprovedFilters,
      ProposalsApprovedResume,
      ProposalsApprovedButton,
    },
    mixins: [
      mobile,
    ],
    props: {
      perPage: {
        type: Number,
        default: INITIAL.per_page,
      },
      scrollable: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        search: '',
        timeout: null,

        nav: {
          action: ProposalsApprovedFilter,
        },
      };
    },
    computed: {
      ...mapState([
        'auth',
        'proposals',
      ]),

      loading() {
        return this.proposals.list.loading;
      },
      state() {
        if (this.loading) {
          return 'loading';
        }

        if (this.list.length === 0) {
          return 'notfound';
        }

        if (this.scrollable) {
          return 'empty';
        }

        return null;
      },
      filters() {
        return this.proposals.list.filters;
      },
      list() {
        return sort(this.proposals.list.items, this.filters.sort);
      },
    },
    mounted() {
      this.search = this.$route.query.customer;
    },
    methods: {
      vehicle(proposal) {
        return vehicleize(proposal.vehicle_model);
      },
      seller(proposal) {
        if (proposal.seller) {
          return namelize(proposal.seller.name, 2);
        }

        return 'Vendedor desativado';
      },
      store(proposal) {
        if (proposal.store) {
          return proposal.store.name;
        }

        return 'Loja desativada';
      },
      values(attempt) {
        const [installment, balloon] = installments(attempt);

        return `${monetize(attempt.input_financing_in_cents)} + ${installment.value} ${balloon ? '+ balões' : ''}`;
      },

      link(proposal) {
        return {
          name: 'proposals-read',
          params: {
            id: proposal.id,
          },
        };
      },

      integrated(attempt) {
        return find(attempt.bank) && attempt.bank_proposal_identifier;
      },

      searching() {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(this.searched, 1000);
      },
      searched() {
        if (typeof this.resetApproved === 'function') {
          this.resetApproved({
            ...this.filters,
            customer: this.search,
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .proposals-approved-filters {
    .cordel-section-item {
      margin-top: var(--cordel-spacing-xl);

      @media all and (max-width: $cordel-breakpoint-mobile) {
        margin-top: var(--cordel-spacing-xxxs);

        &:first-child {
          margin-top: 0;
        }
      }
    }
  }

  .proposals-approved-vertical {
    .cordel-information-line-value {
      @media all and (max-width: $cordel-breakpoint-mobile) {
        margin-left: 0;
        margin-top: var(--cordel-spacing-nano);
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .proposals-approved {
  }

  .proposals-approved-list {
    list-style: none;
    margin-top: var(--cordel-spacing-xl);
    overflow: hidden;
  }

  .proposals-approved-item {
  }

  .proposals-approved-separator {
    background-color: var(--cordel-color-neutral-dark);
    display: block;
    height: var(--cordel-border-width-soft);
    margin: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: 0 var(--product--pwa--horizontal);
    }
  }

  .proposals-approved-link {
    display: block;
    padding: var(--cordel-spacing-xl) var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xl) var(--product--pwa--horizontal);
    }

    &:hover {
      background: var(--cordel-color-neutral-lighter);
    }

    &:focus {
      box-shadow: inset 0 0 0 var(--cordel-border-width-thin) var(--cordel-color-highlight-primary);
      outline: none;
    }
  }

  .proposals-approved-line {
    @media all and (max-width: $cordel-breakpoint-mobile) {
      font-size: var(--cordel-font-size-s);
    }
  }

  .proposals-approved-seller {
    margin-top: var(--cordel-spacing-mini);
  }

  .proposals-approved-vertical {
    margin-top: var(--cordel-spacing-s);

    & + & {
      margin-top: var(--cordel-spacing-nano);
    }

    @media all and (max-width: $cordel-breakpoint-mobile) {
      & + & {
        margin-top: var(--cordel-spacing-s);
      }
    }
  }

  .proposals-approved-vertical {
    @media all and (max-width: $cordel-breakpoint-mobile) {
      align-items: stretch !important;
      flex-direction: column;
    }
  }

  .proposals-approved-label {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .proposals-approved-tag {
    margin-left: var(--cordel-spacing-xxxs);
    flex-shrink: 0;
  }

  .proposals-approved-search {
    margin: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: var(--cordel-spacing-xl) var(--product--pwa--horizontal);
    }
  }

  .proposals-approved-resume {
    margin: var(--cordel-spacing-xl) var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: var(--cordel-spacing-xl) var(--product--pwa--horizontal);
    }
  }
</style>
